import styled, { keyframes, css } from 'styled-components';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

// ============================================
// CONSTANTS
// ============================================

export const DIALER_COLORS = {
  primary: '#1890ff',
  success: '#52c41a',
  agentGreen: '#1B5E20',
  agentGreenLight: '#2E7D32',
  favorite: '#faad14',
  favoriteLight: '#fffbe6',
  background: '#fafafa',
  border: '#e8e8e8',
  borderLight: '#f0f0f0',
  text: '#262626',
  textMedium: '#595959',
  textMuted: '#8c8c8c',
  white: '#ffffff',
  overlay: 'rgba(0, 0, 0, 0.45)',
  error: '#ff4d4f',
  warning: '#faad14',
};

export const COUNTRY_CODES = [
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'United States' },
  { code: '+1', country: 'CA', flag: '🇨🇦', name: 'Canada' },
  { code: '+44', country: 'UK', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+52', country: 'MX', flag: '🇲🇽', name: 'Mexico' },
  { code: '+61', country: 'AU', flag: '🇦🇺', name: 'Australia' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Germany' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'France' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Spain' },
  { code: '+91', country: 'IN', flag: '🇮🇳', name: 'India' },
  { code: '+86', country: 'CN', flag: '🇨🇳', name: 'China' },
];

export const SMS_TEMPLATES = [
  { id: 1, text: "Hi! Just tried calling - call me back when free" },
  { id: 2, text: "Following up on our conversation. Any questions?" },
  { id: 3, text: "Reminder: Your scheduled interview is coming up" },
  { id: 4, text: "Good news! I have a new opportunity that matches your profile" },
  { id: 5, text: "Please call me back at your earliest convenience" },
];

// ============================================
// MODAL OVERLAY & CONTAINER
// ============================================

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${DIALER_COLORS.overlay};
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;
`;

export const ModalContainer = styled.div`
  background: ${DIALER_COLORS.white};
  border-radius: 16px;
  width: 720px;
  max-width: 95vw;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: ${slideUp} 0.3s ease-out;
  display: flex;
  flex-direction: column;
`;

export const ModalHeader = styled.div`
  background: linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #1B5E20 100%);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .header-title {
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${DIALER_COLORS.white};
    font-weight: 600;
    font-size: 16px;
    
    svg {
      font-size: 18px;
    }
  }
`;

export const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: ${DIALER_COLORS.white};
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  svg {
    font-size: 16px;
  }
`;

export const ModalBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

// ============================================
// LEFT PANEL - DIAL PAD
// ============================================

export const LeftPanel = styled.div`
  width: 320px;
  min-width: 320px;
  padding: 20px;
  border-right: 1px solid ${DIALER_COLORS.border};
  display: flex;
  flex-direction: column;
  background: ${DIALER_COLORS.white};
  
  @media (max-width: 640px) {
    width: 100%;
    min-width: 100%;
    border-right: none;
    border-bottom: 1px solid ${DIALER_COLORS.border};
  }
`;

export const CallTypeToggle = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

export const CallTypeButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  border: 2px solid ${props => props.$active 
    ? (props.$type === 'agent' ? DIALER_COLORS.agentGreen : DIALER_COLORS.primary) 
    : DIALER_COLORS.border};
  background: ${props => props.$active 
    ? (props.$type === 'agent' ? 'rgba(27, 94, 32, 0.08)' : 'rgba(24, 144, 255, 0.08)') 
    : DIALER_COLORS.white};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$active 
    ? (props.$type === 'agent' ? DIALER_COLORS.agentGreen : DIALER_COLORS.primary) 
    : DIALER_COLORS.textMuted};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.$type === 'agent' ? DIALER_COLORS.agentGreen : DIALER_COLORS.primary};
  }
  
  svg {
    font-size: 14px;
  }
`;

export const SelectGroup = styled.div`
  margin-bottom: 12px;
  
  .select-label {
    font-size: 11px;
    font-weight: 600;
    color: ${DIALER_COLORS.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }
`;

export const StyledSelect = styled.div`
  position: relative;
  
  select {
    width: 100%;
    padding: 10px 36px 10px 12px;
    border: 1px solid ${DIALER_COLORS.border};
    border-radius: 8px;
    background: ${DIALER_COLORS.white};
    font-size: 14px;
    color: ${DIALER_COLORS.text};
    cursor: pointer;
    appearance: none;
    transition: border-color 0.15s ease;
    
    &:hover, &:focus {
      border-color: ${DIALER_COLORS.primary};
      outline: none;
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid ${DIALER_COLORS.textMuted};
    pointer-events: none;
  }
`;

export const SelectRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  
  ${SelectGroup} {
    margin-bottom: 0;
    flex: 1;
  }
`;

export const PhoneDisplay = styled.div`
  background: ${DIALER_COLORS.background};
  border: 1px solid ${DIALER_COLORS.border};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease;
  
  &:hover {
    border-color: ${DIALER_COLORS.primary};
  }
  
  .phone-number {
    font-size: 24px;
    font-weight: 500;
    color: ${DIALER_COLORS.text};
    letter-spacing: 1px;
    display: flex;
    align-items: center;
    
    .country-code {
      color: ${DIALER_COLORS.textMuted};
      margin-right: 4px;
    }
    
    .cursor {
      width: 2px;
      height: 24px;
      background: ${DIALER_COLORS.primary};
      margin-left: 2px;
      animation: ${blink} 1s infinite;
    }
  }
  
  .placeholder {
    color: ${DIALER_COLORS.textMuted};
    font-size: 18px;
  }
  
  .contact-match {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    font-size: 13px;
    color: ${DIALER_COLORS.success};
    
    svg {
      font-size: 12px;
    }
  }
`;

export const DialPad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 16px;
`;

export const DialButton = styled.button`
  aspect-ratio: 1;
  border: none;
  background: ${DIALER_COLORS.background};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${DIALER_COLORS.border};
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
    background: ${DIALER_COLORS.primary};
    color: ${DIALER_COLORS.white};
    
    .sub-text {
      color: rgba(255, 255, 255, 0.7);
    }
  }
  
  .number {
    font-size: 24px;
    font-weight: 500;
    color: ${DIALER_COLORS.text};
    line-height: 1;
  }
  
  .sub-text {
    font-size: 10px;
    color: ${DIALER_COLORS.textMuted};
    margin-top: 2px;
    letter-spacing: 1px;
  }
`;

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: auto;
`;

export const ActionButton = styled.button`
  width: ${props => props.$size === 'large' ? '64px' : '44px'};
  height: ${props => props.$size === 'large' ? '64px' : '44px'};
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  
  ${props => props.$variant === 'call' && css`
    background: ${DIALER_COLORS.success};
    color: ${DIALER_COLORS.white};
    
    &:hover:not(:disabled) {
      background: #45a618;
      transform: scale(1.05);
    }
    
    &:disabled {
      background: ${DIALER_COLORS.border};
      cursor: not-allowed;
    }
    
    svg {
      font-size: 24px;
    }
  `}
  
  ${props => props.$variant === 'backspace' && css`
    background: ${DIALER_COLORS.background};
    color: ${DIALER_COLORS.text};
    
    &:hover:not(:disabled) {
      background: ${DIALER_COLORS.border};
    }
    
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    
    svg {
      font-size: 18px;
    }
  `}
  
  ${props => props.$variant === 'favorite' && css`
    background: ${props.$active ? DIALER_COLORS.favoriteLight : DIALER_COLORS.background};
    color: ${props.$active ? DIALER_COLORS.favorite : DIALER_COLORS.textMuted};
    
    &:hover:not(:disabled) {
      background: ${DIALER_COLORS.favoriteLight};
      color: ${DIALER_COLORS.favorite};
    }
    
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    
    svg {
      font-size: 18px;
    }
  `}
`;

// ============================================
// RIGHT PANEL - TABS
// ============================================

export const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${DIALER_COLORS.white};
  overflow: hidden;
`;

export const TabHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${DIALER_COLORS.border};
  background: ${DIALER_COLORS.white};
`;

export const Tab = styled.button`
  flex: 1;
  padding: 12px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$active ? DIALER_COLORS.primary : DIALER_COLORS.textMuted};
  border-bottom: 2px solid ${props => props.$active ? DIALER_COLORS.primary : 'transparent'};
  transition: all 0.15s ease;
  
  &:hover {
    color: ${DIALER_COLORS.primary};
    background: rgba(24, 144, 255, 0.04);
  }
  
  svg {
    font-size: 14px;
  }
`;

export const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: ${DIALER_COLORS.background};
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  color: ${DIALER_COLORS.textMuted};
  
  svg {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
  }
  
  .title {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
    color: ${DIALER_COLORS.textMedium};
  }
  
  .subtitle {
    font-size: 12px;
    text-align: center;
    max-width: 200px;
  }
`;

// ============================================
// RECENT & FAVORITES LIST
// ============================================

export const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${DIALER_COLORS.white};
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid ${DIALER_COLORS.borderLight};
  
  &:hover {
    border-color: ${DIALER_COLORS.primary};
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const ListItemAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$color || DIALER_COLORS.background};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: ${props => props.$textColor || DIALER_COLORS.text};
`;

export const ListItemContent = styled.div`
  flex: 1;
  min-width: 0;
  
  .primary {
    font-size: 14px;
    font-weight: 500;
    color: ${DIALER_COLORS.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .secondary {
    font-size: 12px;
    color: ${DIALER_COLORS.textMuted};
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    
    .agent-name {
      color: ${DIALER_COLORS.agentGreen};
      font-weight: 500;
    }
  }
`;

export const ListItemAction = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.$active ? DIALER_COLORS.favorite : DIALER_COLORS.textMuted};
  cursor: pointer;
  padding: 8px;
  transition: all 0.15s ease;
  
  &:hover {
    color: ${DIALER_COLORS.favorite};
    transform: scale(1.1);
  }
  
  svg {
    font-size: 16px;
  }
`;

// ============================================
// CONTACT PREVIEW CARD
// ============================================

export const ContactCard = styled.div`
  background: ${DIALER_COLORS.white};
  border-radius: 12px;
  border: 1px solid ${DIALER_COLORS.borderLight};
  overflow: hidden;
`;

export const ContactHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid ${DIALER_COLORS.borderLight};
`;

export const ContactAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${DIALER_COLORS.primary};
  color: ${DIALER_COLORS.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
`;

export const ContactInfo = styled.div`
  flex: 1;
  
  .name {
    font-size: 16px;
    font-weight: 600;
    color: ${DIALER_COLORS.text};
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .view-profile {
    font-size: 12px;
    color: ${DIALER_COLORS.primary};
    cursor: pointer;
    margin-top: 2px;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.$status) {
      case 'Active':
        return css`
          background: #e6fffb;
          color: #13c2c2;
        `;
      case 'Lead':
        return css`
          background: #e6f7ff;
          color: #1890ff;
        `;
      case 'Interview':
        return css`
          background: #fff7e6;
          color: #fa8c16;
        `;
      case 'Hired':
        return css`
          background: #f6ffed;
          color: #52c41a;
        `;
      default:
        return css`
          background: ${DIALER_COLORS.background};
          color: ${DIALER_COLORS.textMuted};
        `;
    }
  }}
`;

export const ContactDetails = styled.div`
  padding: 12px 16px;
  
  .detail-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    font-size: 13px;
    color: ${DIALER_COLORS.textMedium};
    border-bottom: 1px solid ${DIALER_COLORS.borderLight};
    
    &:last-child {
      border-bottom: none;
    }
    
    svg {
      color: ${DIALER_COLORS.textMuted};
      width: 16px;
    }
  }
`;

// ============================================
// COLLAPSIBLE SECTIONS
// ============================================

export const CollapsibleSection = styled.div`
  margin-top: 12px;
`;

export const CollapsibleHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${DIALER_COLORS.white};
  border: 1px solid ${DIALER_COLORS.borderLight};
  border-radius: ${props => props.$open ? '12px 12px 0 0' : '12px'};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${DIALER_COLORS.background};
  }
  
  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${DIALER_COLORS.text};
    
    svg {
      color: ${DIALER_COLORS.textMuted};
    }
  }
  
  .chevron {
    color: ${DIALER_COLORS.textMuted};
    transition: transform 0.2s ease;
    transform: rotate(${props => props.$open ? '180deg' : '0deg'});
  }
`;

export const CollapsibleContent = styled.div`
  background: ${DIALER_COLORS.white};
  border: 1px solid ${DIALER_COLORS.borderLight};
  border-top: none;
  border-radius: 0 0 12px 12px;
  padding: 16px;
  display: ${props => props.$open ? 'block' : 'none'};
`;

// ============================================
// SMS COMPOSER
// ============================================

export const TemplateDropdown = styled.div`
  margin-bottom: 12px;
  
  select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${DIALER_COLORS.border};
    border-radius: 8px;
    font-size: 13px;
    color: ${DIALER_COLORS.text};
    background: ${DIALER_COLORS.white};
    cursor: pointer;
    
    &:focus {
      outline: none;
      border-color: ${DIALER_COLORS.primary};
    }
  }
`;

export const MessageTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid ${DIALER_COLORS.border};
  border-radius: 8px;
  font-size: 13px;
  color: ${DIALER_COLORS.text};
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${DIALER_COLORS.primary};
  }
  
  &::placeholder {
    color: ${DIALER_COLORS.textMuted};
  }
`;

export const CharCounter = styled.div`
  text-align: right;
  font-size: 11px;
  color: ${props => props.$over ? DIALER_COLORS.error : DIALER_COLORS.textMuted};
  margin-top: 4px;
`;

export const SendButton = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: ${props => props.$success ? DIALER_COLORS.success : DIALER_COLORS.primary};
  color: ${DIALER_COLORS.white};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  transition: all 0.15s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    background: ${DIALER_COLORS.border};
    cursor: not-allowed;
  }
  
  svg {
    font-size: 14px;
  }
`;

// ============================================
// TASK FORM
// ============================================

export const FormGroup = styled.div`
  margin-bottom: 12px;
  
  label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: ${DIALER_COLORS.textMedium};
    margin-bottom: 6px;
  }
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${DIALER_COLORS.border};
  border-radius: 8px;
  font-size: 13px;
  color: ${DIALER_COLORS.text};
  
  &:focus {
    outline: none;
    border-color: ${DIALER_COLORS.primary};
  }
  
  &::placeholder {
    color: ${DIALER_COLORS.textMuted};
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 10px 12px;
  border: 1px solid ${DIALER_COLORS.border};
  border-radius: 8px;
  font-size: 13px;
  color: ${DIALER_COLORS.text};
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${DIALER_COLORS.primary};
  }
  
  &::placeholder {
    color: ${DIALER_COLORS.textMuted};
  }
`;

export const TaskTypeToggle = styled.div`
  display: flex;
  gap: 8px;
`;

export const TaskTypeButton = styled.button`
  flex: 1;
  padding: 10px;
  border: 1px solid ${props => props.$active ? DIALER_COLORS.primary : DIALER_COLORS.border};
  background: ${props => props.$active ? 'rgba(24, 144, 255, 0.08)' : DIALER_COLORS.white};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: ${props => props.$active ? DIALER_COLORS.primary : DIALER_COLORS.textMuted};
  transition: all 0.15s ease;
  
  &:hover {
    border-color: ${DIALER_COLORS.primary};
  }
`;

export const DateTimeRow = styled.div`
  display: flex;
  gap: 8px;
  
  ${FormGroup} {
    flex: 1;
  }
`;

// ============================================
// TASKS PANEL
// ============================================

export const TaskFilters = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

export const TaskFilterButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.$active ? DIALER_COLORS.primary : DIALER_COLORS.border};
  background: ${props => props.$active ? DIALER_COLORS.primary : DIALER_COLORS.white};
  color: ${props => props.$active ? DIALER_COLORS.white : DIALER_COLORS.textMedium};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    border-color: ${DIALER_COLORS.primary};
  }
`;

export const TaskList = styled.div`
  max-height: 280px;
  overflow-y: auto;
`;

export const TaskCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: ${DIALER_COLORS.white};
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid ${props => props.$overdue ? 'rgba(255, 77, 79, 0.3)' : DIALER_COLORS.borderLight};
  background: ${props => props.$overdue ? 'rgba(255, 77, 79, 0.02)' : DIALER_COLORS.white};
  
  &:hover {
    border-color: ${DIALER_COLORS.primary};
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
  }
`;

export const TaskIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${props => props.$type === 'callback' ? 'rgba(82, 196, 26, 0.1)' : 'rgba(24, 144, 255, 0.1)'};
  color: ${props => props.$type === 'callback' ? DIALER_COLORS.success : DIALER_COLORS.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    font-size: 16px;
  }
`;

export const TaskContent = styled.div`
  flex: 1;
  min-width: 0;
  
  .title {
    font-size: 14px;
    font-weight: 500;
    color: ${DIALER_COLORS.text};
    margin-bottom: 4px;
  }
  
  .due-date {
    font-size: 12px;
    color: ${props => props.$overdue ? DIALER_COLORS.error : DIALER_COLORS.textMuted};
    display: flex;
    align-items: center;
    gap: 4px;
    
    svg {
      font-size: 12px;
    }
  }
`;

export const TaskCheckbox = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid ${props => props.$checked ? DIALER_COLORS.success : DIALER_COLORS.border};
  background: ${props => props.$checked ? DIALER_COLORS.success : 'transparent'};
  color: ${DIALER_COLORS.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  
  &:hover {
    border-color: ${DIALER_COLORS.success};
  }
  
  svg {
    font-size: 14px;
  }
`;

export const RefreshButton = styled.button`
  background: transparent;
  border: none;
  color: ${DIALER_COLORS.textMuted};
  cursor: pointer;
  padding: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    color: ${DIALER_COLORS.primary};
  }
  
  svg {
    font-size: 16px;
  }
`;

export const TaskHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  
  .title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${DIALER_COLORS.text};
    
    svg {
      color: ${DIALER_COLORS.textMuted};
    }
  }
`;
